import React, { createContext, useContext, useState, ReactNode } from 'react';

import { logger } from '../utils/logger';
/**
 * Global AI Assistant Context
 * Provides AI assistant state and actions throughout the app
 * Can be called from any screen with context
 */

interface AIAssistantContextType {
  isOpen: boolean;
  context: any;
  provider: string;
  knowledgeBase: string | null;
  openAI: (context: any) => void;
  closeAI: () => void;
  setProvider: (provider: string) => void;
  setKnowledgeBase: (kb: string | null) => void;
}

const AIAssistantContext = createContext<AIAssistantContextType | undefined>(undefined);

interface AIAssistantProviderProps {
  children: ReactNode;
}

export const AIAssistantProvider: React.FC<AIAssistantProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState<any>(null);
  const [provider, setProvider] = useState('openai');
  const [knowledgeBase, setKnowledgeBase] = useState<string | null>(null);

  const openAI = (ctx: any) => {
    setContext(ctx);
    setIsOpen(true);
    logger.debug('AI Assistant opened', { screenName: ctx?.screenName });
  };

  const closeAI = () => {
    setIsOpen(false);
    setContext(null);
    logger.info('❌ AI Assistant closed');
  };

  return (
    <AIAssistantContext.Provider
      value={{ 
        isOpen, 
        context, 
        provider, 
        knowledgeBase, 
        openAI, 
        closeAI, 
        setProvider, 
        setKnowledgeBase 
      }}
    >
      {children}
    </AIAssistantContext.Provider>
  );
};

/**
 * Hook to use AI Assistant from any component
 * Usage:
 * const ai = useAIAssistant();
 * ai.openAI({ screenName: 'Flow Editor', flowName: 'Login Test', script: '...' });
 */
export const useAIAssistant = () => {
  const context = useContext(AIAssistantContext);
  if (!context) {
    throw new Error('useAIAssistant must be used within AIAssistantProvider');
  }
  return context;
};

